// SkipStockTypesPage — /skip/settings/stock-types
//
// Two-panel layout:
//   - LEFT: list of stock types (Skip / Bin / RoRo / custom) with inline
//     edit + add form. Prefix is frozen after creation.
//   - RIGHT: sizes of the selected stock type with inline edit + add form.
//     Empty when no type is selected.

import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Pencil, Plus, Save, Trash2, X } from 'lucide-react';

import { Button } from '../../../components/ui/Button/Button';
import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { useNavigate } from 'react-router-dom';
import {
  useActivateGlobalStockType,
  useCreateSkipStockSize,
  useCreateSkipStockType,
  useDeactivateGlobalStockType,
  useDeleteSkipStockSize,
  useDeleteSkipStockType,
  useSkipStockSizes,
  useSkipStockTypes,
  useUpdateSkipStockSize,
  useUpdateSkipStockType,
} from './hooks/useSkipStock';
import type { SkipStockSize, SkipStockType } from '../../../types/api';

import styles from './SkipStockTypesPage.module.css';

// =================================================================== TYPES PANEL

interface NewTypeForm {
  name: string;
  prefix: string;
  description: string;
}

const EMPTY_NEW_TYPE: NewTypeForm = { name: '', prefix: '', description: '' };

interface TypeCardProps {
  stockType: SkipStockType;
  selected: boolean;
  onSelect: () => void;
}

function TypeCard({ stockType, selected, onSelect }: TypeCardProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stockType.name);
  const [description, setDescription] = useState(stockType.description ?? '');
  const [error, setError] = useState<string | null>(null);
  // Optimistic override for the activated toggle on globals. Set the
  // moment the user clicks; cleared on settle (success → server value
  // catches up via invalidate; error → revert).
  const [optimisticActivated, setOptimisticActivated] = useState<boolean | null>(null);

  const updateMutation = useUpdateSkipStockType(stockType.enc_id);
  const deleteMutation = useDeleteSkipStockType(stockType.enc_id);
  const activateMutation = useActivateGlobalStockType();
  const deactivateMutation = useDeactivateGlobalStockType();

  const isGlobal = stockType.is_global;
  const serverActivated = stockType.activated === true;
  const effectiveActivated = optimisticActivated ?? serverActivated;
  const isDeactivatedGlobal = isGlobal && !effectiveActivated;

  function handleSave() {
    setError(null);
    updateMutation.mutate(
      { name, description: description || null },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err.message ?? 'Could not save'),
      },
    );
  }

  function handleToggleActive() {
    setError(null);
    updateMutation.mutate(
      { is_active: !stockType.is_active },
      { onError: (err) => setError(err.message ?? 'Could not save') },
    );
  }

  function handleDelete() {
    setError(null);
    deleteMutation.mutate(undefined, {
      onError: (err) => setError(err.message ?? 'Could not delete'),
    });
  }

  function handleToggleActivated() {
    setError(null);
    const next = !effectiveActivated;
    setOptimisticActivated(next);
    const mutation = next ? activateMutation : deactivateMutation;
    mutation.mutate(
      { encId: stockType.enc_id },
      {
        onSuccess: () => setOptimisticActivated(null),
        onError: (err) => {
          setOptimisticActivated(null);
          setError(err.message ?? (next ? 'Could not activate' : 'Could not deactivate'));
        },
      },
    );
  }

  return (
    <div
      className={[
        styles.typeCard,
        selected ? styles.typeCardActive : '',
        isDeactivatedGlobal ? styles.typeCardDeactivated : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
    >
      <div className={styles.typeCardHeader}>
        <span className={styles.typeName}>
          {stockType.name}
          {isGlobal ? <span className={styles.defaultBadge}>Default</span> : null}
          <span
            className={[styles.prefixBadge, isGlobal ? styles.prefixBadgeMuted : '']
              .filter(Boolean)
              .join(' ')}
          >
            [{stockType.prefix}]
          </span>
        </span>
        {stockType.is_active ? null : <span className={styles.muted}>Inactive</span>}
      </div>
      {editing ? null : (
        <>
          {stockType.description ? (
            <div className={styles.typeDescription}>{stockType.description}</div>
          ) : null}
          <div className={styles.typeMeta}>
            {stockType.sizes_count} size{stockType.sizes_count === 1 ? '' : 's'}
          </div>
        </>
      )}

      {editing ? (
        <div className={styles.editForm} onClick={(e) => e.stopPropagation()}>
          <label className={styles.fullSpan}>
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
          </label>
          <label className={styles.fullSpan}>
            <span>Description</span>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      ) : null}

      {/* Global defaults: per-tenant activation toggle (no edit/delete).
          Own-tenant types: existing active/edit/delete row. */}
      {isGlobal ? (
        <div className={styles.actionRow} onClick={(e) => e.stopPropagation()}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={effectiveActivated}
              onChange={handleToggleActivated}
              disabled={activateMutation.isPending || deactivateMutation.isPending}
              aria-label={`Activated ${stockType.name}`}
            />
            {effectiveActivated ? 'Activated' : 'Not activated'}
          </label>
        </div>
      ) : (
        <div className={styles.actionRow} onClick={(e) => e.stopPropagation()}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={stockType.is_active}
              onChange={handleToggleActive}
              aria-label={`Active ${stockType.name}`}
            />
            Active
          </label>
          <span className={styles.spacer} />
          {editing ? (
            <>
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
              <Button variant="ghost" icon={X} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={handleDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      )}
      {!editing && error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

interface AddTypeFormProps {
  existingPrefixes: string[];
}

function AddTypeForm({ existingPrefixes }: AddTypeFormProps): JSX.Element {
  const [form, setForm] = useState<NewTypeForm>(EMPTY_NEW_TYPE);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateSkipStockType();

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.prefix.trim()) {
      setError('Name and prefix are required.');
      return;
    }
    if (existingPrefixes.includes(form.prefix.toUpperCase())) {
      setError(`Prefix "${form.prefix.toUpperCase()}" is already in use.`);
      return;
    }
    if (!/^[A-Z0-9]+$/.test(form.prefix)) {
      setError('Prefix must be uppercase letters/digits only.');
      return;
    }
    create.mutate(
      {
        name: form.name.trim(),
        prefix: form.prefix,
        description: form.description.trim() || null,
      },
      {
        onSuccess: () => setForm(EMPTY_NEW_TYPE),
        onError: (err) => setError(err.message ?? 'Could not create stock type'),
      },
    );
  }

  return (
    <form className={styles.addForm} onSubmit={handleSubmit} aria-label="Add Stock Type">
      <label>
        <span>Name</span>
        <input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={100}
          placeholder="e.g. Crusher"
        />
      </label>
      <label>
        <span>Prefix</span>
        <input
          required
          value={form.prefix}
          onChange={(e) =>
            setForm({ ...form, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })
          }
          maxLength={10}
          placeholder="e.g. CRU"
          aria-label="Prefix"
        />
      </label>
      <label className={styles.addFormFull}>
        <span>Description (optional)</span>
        <input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={500}
        />
      </label>
      {error ? <p className={[styles.error, styles.addFormFull].join(' ')}>{error}</p> : null}
      <div className={styles.addFormActions}>
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Add Stock Type
        </Button>
      </div>
    </form>
  );
}

// =================================================================== SIZES PANEL

interface SizeRowProps {
  typeEncId: string;
  size: SkipStockSize;
}

function SizeRow({ typeEncId, size }: SizeRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(size.name);
  const [capacity, setCapacity] = useState(size.typical_capacity ?? '');
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateSkipStockSize(typeEncId, size.enc_id);
  const deleteMutation = useDeleteSkipStockSize(typeEncId, size.enc_id);

  function handleSave() {
    setError(null);
    updateMutation.mutate(
      { name, typical_capacity: capacity || null },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(err.message ?? 'Could not save'),
      },
    );
  }

  function handleToggleActive() {
    setError(null);
    updateMutation.mutate(
      { is_active: !size.is_active },
      { onError: (err) => setError(err.message ?? 'Could not save') },
    );
  }

  function handleDelete() {
    setError(null);
    deleteMutation.mutate(undefined, {
      onError: (err) => setError(err.message ?? 'Could not delete'),
    });
  }

  const isGlobal = size.is_global;

  return (
    <div className={styles.sizeRow}>
      {editing && !isGlobal ? (
        <div style={{ flex: 1 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Size name"
            style={{ width: '100%', padding: '4px 8px', marginBottom: '4px' }}
          />
          <input
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            aria-label="Typical capacity"
            placeholder="Typical capacity (optional)"
            style={{ width: '100%', padding: '4px 8px' }}
          />
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      ) : (
        <div>
          <div className={styles.sizeName}>{size.name}</div>
          {size.typical_capacity ? (
            <div className={styles.sizeMeta}>{size.typical_capacity}</div>
          ) : null}
          {!size.is_active ? <div className={styles.muted}>Inactive</div> : null}
          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      )}
      {/* Global default sizes are read-only — no toggle / edit / delete. */}
      {isGlobal ? null : (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={size.is_active}
              onChange={handleToggleActive}
              aria-label={`Active ${size.name}`}
            />
            Active
          </label>
          {editing ? (
            <>
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
              <Button variant="ghost" icon={X} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={handleDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface AddSizeFormProps {
  typeEncId: string;
}

function AddSizeForm({ typeEncId }: AddSizeFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateSkipStockSize(typeEncId);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        typical_capacity: capacity.trim() || null,
        description: description.trim() || null,
      },
      {
        onSuccess: () => {
          setName('');
          setCapacity('');
          setDescription('');
        },
        onError: (err) => setError(err.message ?? 'Could not create size'),
      },
    );
  }

  return (
    <form className={styles.addForm} onSubmit={handleSubmit} aria-label="Add Size">
      <label>
        <span>Name</span>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          placeholder="e.g. 8 Yard"
        />
      </label>
      <label>
        <span>Typical Capacity</span>
        <input
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          maxLength={100}
          placeholder="e.g. 8 cubic yards"
        />
      </label>
      <label className={styles.addFormFull}>
        <span>Description (optional)</span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
        />
      </label>
      {error ? <p className={[styles.error, styles.addFormFull].join(' ')}>{error}</p> : null}
      <div className={styles.addFormActions}>
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Add Size
        </Button>
      </div>
    </form>
  );
}

// =================================================================== PAGE

export function SkipStockTypesPage(): JSX.Element {
  const navigate = useNavigate();
  const typesQuery = useSkipStockTypes();
  const [selectedTypeEncId, setSelectedTypeEncId] = useState<string | null>(null);

  const types = typesQuery.data?.stockTypes ?? [];
  const selectedType = types.find((t) => t.enc_id === selectedTypeEncId) ?? null;

  // Auto-select the first type once data lands.
  useEffect(() => {
    if (selectedTypeEncId === null && types.length > 0) {
      setSelectedTypeEncId(types[0]!.enc_id);
    }
  }, [types, selectedTypeEncId]);

  const sizesQuery = useSkipStockSizes(selectedTypeEncId);
  const sizes = sizesQuery.data?.sizes ?? [];

  const existingPrefixes = types.map((t) => t.prefix.toUpperCase());

  return (
    <div className={styles.page}>
      <PageHeader
        title="Stock Types"
        subtitle="Manage your skip, bin, roro and custom stock types and their sizes"
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/skip/dashboard')}>
            Back
          </Button>
        }
      />

      <div className={styles.grid}>
        <Card>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Stock Types</span>
          </div>
          {typesQuery.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : types.length === 0 ? (
            <p className={styles.empty}>No stock types yet — add your first below.</p>
          ) : (
            <div className={styles.cardList}>
              {types.map((t) => (
                <TypeCard
                  key={t.enc_id}
                  stockType={t}
                  selected={t.enc_id === selectedTypeEncId}
                  onSelect={() => setSelectedTypeEncId(t.enc_id)}
                />
              ))}
            </div>
          )}
          <AddTypeForm existingPrefixes={existingPrefixes} />
        </Card>

        <Card>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>
              {selectedType
                ? `${selectedType.name} — Sizes`
                : 'Sizes'}
            </span>
          </div>
          {selectedType !== null && selectedType.is_global ? (
            <p className={styles.globalBanner}>
              This is a default stock type. You can add your own stock types and
              sizes in addition to these defaults.
            </p>
          ) : null}
          {selectedType === null ? (
            <p className={styles.empty}>Select a stock type to manage its sizes.</p>
          ) : sizesQuery.isLoading ? (
            <p className={styles.empty}>Loading sizes…</p>
          ) : sizes.length === 0 ? (
            <p className={styles.empty}>No sizes for this stock type yet — add one below.</p>
          ) : (
            <div>
              {sizes.map((s) => (
                <SizeRow key={s.enc_id} typeEncId={selectedType.enc_id} size={s} />
              ))}
            </div>
          )}
          {selectedType !== null && !selectedType.is_global ? (
            <AddSizeForm typeEncId={selectedType.enc_id} />
          ) : null}
        </Card>
      </div>
    </div>
  );
}
